// src/helper/jobs/activeJobHelper.js
//
// Pure helpers behind ActiveJobContext: the one selected "target job" that
// Tailor, Cover Letter, Interview Prep, Application Answers and the tracker
// all read instead of each re-asking for a job description.
//
// SOURCE OF TRUTH IS FIRESTORE, NOT localStorage.
//   The job is persisted as a field on saved-jobs/{uid} (the PARENT document
//   of the tracker's items subcollection). That document is already covered
//   by the security rules, nothing else writes it, and a subcollection query
//   never sees its fields - so putting the pointer there costs no new rule
//   and cannot collide with the tracker's items.
//
//   localStorage keeps a per-uid MIRROR purely so the first paint after a
//   refresh has the job before the Firestore read lands. It is never trusted
//   over Firestore, and every access is guarded (Safari private mode throws).
//
// LEGACY FALLBACKS. Before this context each hand-off used its own channel:
//   tailor       -> jobsForYouTailor:{jobId} + jobsForYouTailorLastJobId
//   cover letter -> jobsForYouCoverLetterTarget
// Those keys still work - the pages read them first - and when the provider
// finds nothing else it adopts the last tailored job so an in-flight user is
// not stranded with an empty selection.

import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { SAVED_JOB_DESCRIPTION_CHARS } from "../../utils/jobs/jobConstants";
import { loadLastJobsTailorCache } from "./jobLocalCacheHelper";

export const ACTIVE_JOB_STORAGE_PREFIX = "activeJob:";
// Field on saved-jobs/{uid}.
export const ACTIVE_JOB_FIELD = "activeJob";
export const ACTIVE_JOB_UPDATED_FIELD = "activeJobUpdatedTime";

// The legacy cover-letter hand-off key. Read by CoverLetterForm as a
// backward-compatible fallback and cleared by clearActiveJob so a stale
// target cannot outlive the selection it belonged to.
export const LEGACY_COVER_LETTER_TARGET_KEY = "jobsForYouCoverLetterTarget";

const MAX_SHORT_TEXT_CHARS = 200;

const safeSetLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const safeGetLocalStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeRemoveLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

const cleanText = (value, maxChars) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .slice(0, maxChars);

export const activeJobStorageKey = (uid) =>
  `${ACTIVE_JOB_STORAGE_PREFIX}${String(uid || "").trim()}`;

/**
 * Reduces any job-like object (a NormalizedJob, a saved-jobs item's `job`
 * snapshot, a saved-jobs item itself, a manual job) to the compact shape the
 * consumers need. Returns null when there is no usable title.
 *
 * The description is clamped to SAVED_JOB_DESCRIPTION_CHARS - the same cap
 * the tracker applies - so the Firestore write stays small and every reader
 * sees the same text the tracker would.
 */
export const normalizeActiveJob = (input) => {
  if (isEmpty(input)) return null;

  // A saved-jobs item wraps the posting under `job` and carries the id at the
  // top level; a bare posting has everything at the top. Accept both.
  const source = !isEmpty(get(input, "job", null)) ? input.job : input;
  const title = cleanText(get(source, "title", ""), MAX_SHORT_TEXT_CHARS);

  if (!title) return null;

  const jobId = cleanText(
    get(input, "jobId", "") || get(source, "jobId", ""),
    MAX_SHORT_TEXT_CHARS
  );
  const description = cleanText(
    get(source, "description", ""),
    SAVED_JOB_DESCRIPTION_CHARS
  );
  const wasTruncated =
    Boolean(get(source, "descriptionTruncated", false)) ||
    String(get(source, "description", "") || "").trim().length >
      SAVED_JOB_DESCRIPTION_CHARS;

  return {
    jobId,
    title,
    company: cleanText(get(source, "company", ""), MAX_SHORT_TEXT_CHARS),
    location: cleanText(get(source, "location", ""), MAX_SHORT_TEXT_CHARS),
    description,
    descriptionTruncated: wasTruncated,
    applyUrl: cleanText(get(source, "applyUrl", ""), 2000),
    jobUrl: cleanText(get(source, "jobUrl", ""), 2000),
    source: cleanText(get(source, "source", ""), MAX_SHORT_TEXT_CHARS),
    setAt: cleanText(get(input, "setAt", ""), 64) || new Date().toISOString(),
  };
};

/**
 * Two selections are "the same job" when their ids match, or - for jobs with
 * no id, such as a pasted posting - when title and company match. Used to
 * avoid a redundant Firestore write when a page re-asserts the job it was
 * handed.
 */
export const isSameActiveJob = (left, right) => {
  if (isEmpty(left) || isEmpty(right)) return false;

  const leftId = cleanText(get(left, "jobId", ""), MAX_SHORT_TEXT_CHARS);
  const rightId = cleanText(get(right, "jobId", ""), MAX_SHORT_TEXT_CHARS);

  if (leftId && rightId) return leftId === rightId;
  if (leftId || rightId) return false;

  return (
    cleanText(get(left, "title", ""), MAX_SHORT_TEXT_CHARS).toLowerCase() ===
      cleanText(get(right, "title", ""), MAX_SHORT_TEXT_CHARS).toLowerCase() &&
    cleanText(get(left, "company", ""), MAX_SHORT_TEXT_CHARS).toLowerCase() ===
      cleanText(get(right, "company", ""), MAX_SHORT_TEXT_CHARS).toLowerCase()
  );
};

/**
 * When `next` is the same job as `previous`, keeps every non-empty field of
 * `previous` that `next` left blank. A route-state copy of a job often has no
 * description while the tracker card it came from did; re-asserting the job
 * must not throw the description away. Different jobs: `next` wins outright.
 */
export const mergeActiveJob = (previous, next) => {
  if (isEmpty(next)) return previous || null;
  if (isEmpty(previous) || !isSameActiveJob(previous, next)) return next;

  const merged = { ...previous };

  Object.keys(next).forEach((key) => {
    const value = next[key];
    const isBlank =
      value === null ||
      value === undefined ||
      value === "" ||
      (key === "descriptionTruncated" && value === false);

    if (!isBlank) merged[key] = value;
  });

  return merged;
};

/**
 * `${company} — ${location}\n\n${description}` with empty parts dropped -
 * the exact shape jobHandoffHelper feeds to Interview Prep and the cover
 * letter, so a page that prefills from the context produces the same prompt
 * text a hand-off would have.
 */
export const buildActiveJobDescriptionText = (job, maxChars = 6000) => {
  if (isEmpty(job)) return "";

  const company = cleanText(get(job, "company", ""), MAX_SHORT_TEXT_CHARS);
  const location = cleanText(get(job, "location", ""), MAX_SHORT_TEXT_CHARS);
  const description = String(get(job, "description", "") || "").trim();

  const header = [company, location].filter(Boolean).join(" — ");

  return [header, description].filter(Boolean).join("\n\n").slice(0, maxChars);
};

/** "Title at Company" / "Title" for banners and labels. */
export const formatActiveJobLabel = (job) => {
  if (isEmpty(job)) return "";

  const title = cleanText(get(job, "title", ""), MAX_SHORT_TEXT_CHARS);
  const company = cleanText(get(job, "company", ""), MAX_SHORT_TEXT_CHARS);

  if (!title) return "";

  return company ? `${title} at ${company}` : title;
};

// ---------------------------------------------------------------------------
// localStorage mirror (first paint only - Firestore wins)
// ---------------------------------------------------------------------------

export const readActiveJobMirror = (uid) => {
  if (!uid) return null;

  const raw = safeGetLocalStorage(activeJobStorageKey(uid));

  if (!raw) return null;

  try {
    return normalizeActiveJob(JSON.parse(raw));
  } catch (error) {
    safeRemoveLocalStorage(activeJobStorageKey(uid));
    return null;
  }
};

export const writeActiveJobMirror = (uid, job) => {
  if (!uid) return false;

  const normalized = normalizeActiveJob(job);

  if (!normalized) return clearActiveJobMirror(uid);

  return safeSetLocalStorage(
    activeJobStorageKey(uid),
    JSON.stringify(normalized)
  );
};

export const clearActiveJobMirror = (uid) => {
  if (!uid) return false;

  return safeRemoveLocalStorage(activeJobStorageKey(uid));
};

// ---------------------------------------------------------------------------
// Legacy hand-off keys
// ---------------------------------------------------------------------------

/**
 * The job the old Tailor hand-off left behind, if any. Adopted by the
 * provider ONLY when Firestore and the mirror are both empty, so a user who
 * was mid-flow when this shipped keeps their selection.
 */
export const readLegacyActiveJob = () => {
  const cached = loadLastJobsTailorCache();
  const job = get(cached, "job", null);

  return normalizeActiveJob(job);
};

export const clearLegacyCoverLetterTarget = () =>
  safeRemoveLocalStorage(LEGACY_COVER_LETTER_TARGET_KEY);

export default {
  ACTIVE_JOB_STORAGE_PREFIX,
  ACTIVE_JOB_FIELD,
  ACTIVE_JOB_UPDATED_FIELD,
  LEGACY_COVER_LETTER_TARGET_KEY,
  activeJobStorageKey,
  normalizeActiveJob,
  isSameActiveJob,
  mergeActiveJob,
  buildActiveJobDescriptionText,
  formatActiveJobLabel,
  readActiveJobMirror,
  writeActiveJobMirror,
  clearActiveJobMirror,
  readLegacyActiveJob,
  clearLegacyCoverLetterTarget,
};
